import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {cloneDeep} from 'lodash'
import {useStatesGlobal} from '../../../../../../_gori/helpers/components/StatesGlobalProvider'
import {
  Action,
  ActionInputType,
  ActionType,
  ActionTypeMetadata,
  OPTION_WEIGHT_UNIT,
} from '../../../../../../_gori/constants'
import {InputDate, InputTextFormik, SelectFormik} from '../../../../../../_gori/partials/widgets'
import {sortBy} from 'lodash'
import {useAutomationRulesData} from '../../../hooks'
import {AddressOptionLabel} from './AddressOptionLabel'
import {KTSVG} from '../../../../../../_gori/helpers'
import moment from 'moment/moment'

type Props = {
  action: Partial<Action>
  index: number
  formik: any
  actionMeta?: ActionTypeMetadata
  ghostShipFromOptions?: {label: string; value: any}[]
}

export const ActionValue: React.FC<Props> = ({
  action,
  index,
  formik,
  actionMeta,
  ghostShipFromOptions = [],
}) => {
  const intl = useIntl()
  const {
    serviceOptions,
    tagOptions,
    optionsPackageTypes,
    optionsCustomPackage,
    shipFromOptions,
    presetOptions,
  } = useAutomationRulesData()
  const {statesGlobal} = useStatesGlobal()
  const addOnServices = statesGlobal.addOnServices

  // Format tag option with icon
  const formatTagOption = (option: any) => {
    return (
      <div className='d-flex align-items-center'>
        <KTSVG
          path='/media/gori/orders/tag.svg'
          className='svg-icon-2 me-2'
          style={{color: option.color}}
        />
        <span>{option.label}</span>
      </div>
    )
  }

  const optionsSignature = useMemo(() => {
    return addOnServices?.signatures || []
  }, [addOnServices?.signatures])

  const optionsExtraService = useMemo(() => {
    return (
      addOnServices?.extra_services?.map((item) => ({...item, value: item.value?.toString()})) || []
    )
  }, [addOnServices?.extra_services])

  const insuranceOptions = useMemo(() => {
    return sortBy(
      addOnServices?.insurances?.map((item) => ({
        label: item.type.toUpperCase(),
        value: item.type,
      })) || [],
      ['label']
    )
  }, [addOnServices?.insurances])
  if (!actionMeta) return null

  const renderMainContent = () => {
    switch (actionMeta.type) {
      case ActionType.SET_SHIPPING_SERVICE:
        return (
          <SelectFormik
            formik={formik}
            name={`actions[${index}].value.service`}
            options={serviceOptions as any}
            isMulti={false}
            emptyDefault={false}
          />
        )

      case ActionType.SET_PACKAGE_TYPE: {
        const pkgValue = formik.values.actions?.[index]?.value
        return (
          <>
            <SelectFormik
              formik={formik}
              name={`actions[${index}].value`}
              options={[...cloneDeep(optionsPackageTypes), optionsCustomPackage] as any}
              isMulti={false}
              emptyDefault={false}
              formatGroupLabel={(group: any) => <span className='fw-bold'>{group.label}</span>}
            />
            {pkgValue?.length != null && (
              <div className='text-muted fs-7 mt-2'>
                {pkgValue.length} x {pkgValue.width} x {pkgValue.height} {pkgValue.dimension_unit}
              </div>
            )}
          </>
        )
      }

      case ActionType.SET_PACKAGE_DIMENSION:
        return (
          <div className='d-flex gap-2 align-items-start'>
            <div className='flex-grow-1'>
              <InputTextFormik
                formik={formik}
                name={`actions[${index}].value.length`}
                type='number'
                min={0}
                placeholder='0'
                inputClassName='text-start'
                checkFormik={false}
                configAppend={{
                  name: 'L',
                  position: 'right',
                  classAppend: 'text-muted fs-7',
                  classInput: 'pe-4',
                }}
              />
            </div>
            <div className='flex-grow-1'>
              <InputTextFormik
                formik={formik}
                name={`actions[${index}].value.width`}
                type='number'
                min={0}
                placeholder='0'
                inputClassName='text-start'
                checkFormik={false}
                configAppend={{
                  name: 'W',
                  position: 'right',
                  classAppend: 'text-muted fs-7',
                  classInput: 'pe-4',
                }}
              />
            </div>
            <div className='flex-grow-1'>
              <InputTextFormik
                formik={formik}
                name={`actions[${index}].value.height`}
                type='number'
                min={0}
                placeholder='0'
                inputClassName='text-start'
                checkFormik={false}
                configAppend={{
                  name: 'H',
                  position: 'right',
                  classAppend: 'text-muted fs-7',
                  classInput: 'pe-4',
                }}
              />
            </div>
          </div>
        )

      case ActionType.SET_WEIGHT:
        return (
          <div className='d-flex gap-3'>
            <div className='flex-grow-1'>
              <label className='form-label'>{intl.formatMessage({id: 'SET_WEIGHT'})}</label>
              <InputTextFormik
                formik={formik}
                name={`actions[${index}].value.weight`}
                type='number'
                min={0}
              />
            </div>
            <div>
              <label className='form-label'>{intl.formatMessage({id: 'SET_UNIT'})}</label>
              <SelectFormik
                formik={formik}
                name={`actions[${index}].value.weight_unit`}
                options={OPTION_WEIGHT_UNIT}
                emptyDefault={false}
                className='w-100px'
              />
            </div>
          </div>
        )

      case ActionType.SET_CONFIRMATION_TYPE:
        return (
          <SelectFormik
            formik={formik}
            name={`actions[${index}].value.confirmation_type`}
            options={optionsSignature as any}
            emptyDefault={false}
          />
        )

      case ActionType.HAZMAT_SERVICE_OPTION:
        return (
          <SelectFormik
            formik={formik}
            name={`actions[${index}].value.extra_service`}
            options={optionsExtraService as any}
            emptyDefault={false}
          />
        )

      case ActionType.INSURE_PACKAGE:
        return (
          <>
            <SelectFormik
              formik={formik}
              name={`actions[${index}].value.insurance_type`}
              options={insuranceOptions as any}
              emptyDefault={false}
              hasUseIntl
            />
            <div className='mt-3'>
              <label className='form-label'>{intl.formatMessage({id: 'DECLARED_VALUE'})}</label>
              <InputTextFormik
                formik={formik}
                name={`actions[${index}].value.insured_amount`}
                type='number'
                min={0}
                configAppend={{name: '$', position: 'left', classInput: 'ps-6'}}
              />
            </div>
          </>
        )

      case ActionType.APPLY_PRESET:
        return (
          <SelectFormik
            formik={formik}
            name={`actions[${index}].value.preset_id`}
            options={presetOptions}
            isMulti={false}
            emptyDefault={false}
          />
        )

      case ActionType.ASSIGN_FROM_ADDRESS:
        return (
          <SelectFormik
            formik={formik}
            name={`actions[${index}].value.shipping_address_id`}
            options={[...shipFromOptions, ...ghostShipFromOptions]}
            isMulti={false}
            emptyDefault={false}
            formatOptionLabel={(option, {selectValue, context}) => (
              <AddressOptionLabel
                option={option}
                isSelected={(selectValue as any[]).some((v) => v.value === option.value)}
                context={context}
              />
            )}
          />
        )

      case ActionType.ADD_TAG:
        return (
          <SelectFormik
            formik={formik}
            name={`actions[${index}].value.tag_ids`}
            options={tagOptions}
            isMulti={false}
            emptyDefault={false}
            formatOptionLabel={formatTagOption}
          />
        )

      default: {
        const valueKey = actionMeta.value_schema ? Object.keys(actionMeta.value_schema)[0] : 'value'
        if (actionMeta.input_type === ActionInputType.DATE) {
          return (
            <InputDate
              formik={formik}
              name={`actions[${index}].value.${valueKey}`}
              min={moment()}
            />
          )
        }
        if (
          actionMeta.input_type === ActionInputType.TEXT ||
          actionMeta.input_type === ActionInputType.TEXTAREA
        ) {
          return (
            <InputTextFormik
              formik={formik}
              name={`actions[${index}].value.${valueKey}`}
              isTextArea={actionMeta.input_type === ActionInputType.TEXTAREA}
            />
          )
        }
        return null
      }
    }
  }

  return <>{renderMainContent()}</>
}
